import { mergeMap } from 'rxjs/operators';
import { catchWithAuthErrors } from '../Helpers/rxjsHelpers';
import { calculateChangeTracking } from './ChangeTracker';
import copy from 'fast-clone';

/*
 * This file includes a custom operator (syncTarget) that can be used to target a specific piece of state,
 * and ensure that as the client writes to client state, this state continually gets compared with server state, the
 * server will be informed and updated appropriately.
 /

/**
 * Enum to describe different runSyncProcess handleSyncCompletion() responses
 * @deprecated This functionality has been rewritten for simplicity and use with typescript.
 */
export const SyncStatus = {
  // Sync Status enum
  success: 'success',
  earlyExit: 'earlyExit',
  errorSuccessfulRecovery: 'errorSuccessfulRecovery',
  errorFailedRecovery: 'errorFailedRecovery',
};

/**
 * Retrieve defaults for sync process
 * These defaults describe the minimal setup for our sync process below to function
 * @deprecated This functionality has been rewritten for simplicity and use with typescript.
 */
export const getSyncTargetDefaults = (defaultPersistent = {}, defaultSideEffects = {}) => ({
  // State that client can write to & read from, writing changes should be saved to server (synchronised)
  syncModel: copy(defaultPersistent),

  // State that when changed should trigger retrieval of new server data (resynchronisation) (e.g. server requests that have a URI query)
  syncParams: copy(defaultSideEffects),

  // Server state
  syncModel_synced: copy(defaultPersistent),

  // State that was used in last server query
  syncParams_synced: copy(defaultSideEffects),
});

/**
 * This will return an observable that when configured will maintain synchronisation between server and client state.
 *
 * @deprecated This functionality has been rewritten for simplicity and use with typescript.
 * @param {*} target - The target state to be synchronised
 * @param {boolean} force - Whether to force a synchronisation
 * @param {function} remoteUpdateFn - The action used to perform the remote get/update call (provided an object { updateModel, syncParams })
 * @returns A stream on sync completion (success or failure) with object: { status, syncPatch, error, recoveryError }
 */
export const syncTarget = (target, force, remoteUpdateFn) => {
  if (target === undefined || remoteUpdateFn === undefined) {
    throw new Error(`syncTarget() requires force, target and remoteUpdate params to be provided.`);
  }

  const {
    syncModel: clientModelBefore,
    syncModel_synced: serverModelBefore,
    syncParams = {},
    syncParams_synced: synchronisedSideEffects = {},
  } = target;

  if (!clientModelBefore || !serverModelBefore) {
    throw new Error(
      `syncProcess option getSyncTarget() should return a state target object that contains expected state sync keys (server, client, etc)`
    );
  }

  const clientModelToUse = clientModelBefore; /* the normal model pulled from live data */

  // Calculate if any changes have been made to syncModel model (and a diff)
  const changesWereMade = calculateChangeTracking(clientModelToUse, serverModelBefore).changeCount > 0;

  // Calculate if any changes have been made to 'syncParams'.
  const sideEffectChangesExist = !!calculateChangeTracking(syncParams, synchronisedSideEffects).changeCount;

  // If it has not been forced and no changes were made, exit early
  // Only exit early if (A) no side effect changes exist AND (B) No changes were made to sync model AND (C) It is not a forced sync
  const shouldExitEarly = !sideEffectChangesExist && !changesWereMade && !force;
  if (shouldExitEarly) return Promise.resolve({ target, status: SyncStatus.earlyExit });

  // If there are pending changes, do a PUT before the GET, otherwise skip straight to the GET
  let serverModelSent = changesWereMade ? copy(clientModelToUse) : undefined;

  let remoteUpdateErrorHandler$ = catchWithAuthErrors((error) => {
    // Something went wrong, attempt a repair (perform a GET and force state back to those results)
    return remoteUpdateFn({ updateModel: false, syncParams }).pipe(
      mergeMap((serverModelReceived) =>
        // Repair succeeded, still report error, reset all state to server state
        Promise.resolve({
          target,
          status: SyncStatus.errorSuccessfulRecovery,
          error,
          syncPatch: { serverModelReceived, syncParams },
        })
      ),
      catchWithAuthErrors((recoveryError) =>
        // Repair failed, report additional error for failure & reset client state to last _known_ server stage
        Promise.resolve({
          target,
          status: SyncStatus.errorFailedRecovery,
          error,
          recoveryError,
          syncPatch: { syncParams },
        })
      )
    );
  });

  return remoteUpdateFn({ updateModel: serverModelSent, syncParams }).pipe(
    mergeMap((serverModelReceived) =>
      Promise.resolve({
        target,
        status: SyncStatus.success,
        syncPatch: {
          status: SyncStatus.success,
          clientModelBefore: clientModelToUse,
          serverModelSent: serverModelSent || serverModelBefore,
          serverModelReceived,
          syncParams,
        },
      })
    ),
    remoteUpdateErrorHandler$
  );
};
